import { OnboardingPart, OnboardingStep, Step } from 'apis/OnboardingAPI';
import { CompletenessPart } from 'apis/CompanyAPI/communities/completenessRules';
import { updateInList } from 'util/arrayUtils';

export function getChecklistStepText(stepId: OnboardingStep) {
  switch (stepId) {
    case 'CompanyInfo':
      return 'Add general company information';
    case 'IndustriesAndStage':
      return 'Add industry and stage';
    case 'CompanyMission':
      return 'Add company mission';
    case 'Kpis':
      return 'Add your KPIs';
    case 'CompanyBranding':
      return 'Upload company logo and banner image';
    case 'ProblemSolutionUnfairAdvantage':
      return 'Add executive summary';
    case 'PitchDeck':
      return 'Upload your pitch deck';
    case 'InviteYourStakeholders':
      return 'Invite shareholders';
    case 'PostFirstUpdate':
      return 'Post first company update';
    case 'InvestmentPreferences':
      return 'Add investment preferences';
    case 'InvestorType':
      return 'Add investor type';
    case 'InvestmentRange':
      return 'Add preferred range & capacity';
    case 'CompanyMatching':
      return 'Follow a few companies to get started (optional)';
    case 'CommunityBranding':
      return 'Upload community logo';
    case 'CommunityDescription':
      return 'Write a description of your community';
    case 'InviteYourTeam':
      return 'Invite your team';
    case 'InviteMembers':
      return 'Invite members';
    case 'ChooseYourRole':
      return 'Choose your role';
    case 'UserProfile':
      return 'Add your role and contact details';
    case 'BasicProfile':
      return 'Complete your basic profile';
    case 'IndustryAndBusinessFocus':
      return 'Add your industry and business focus';
    case 'ProductAndFundingStage':
      return 'Add your product and funding stage';
    case 'OrganizationType':
      return 'Add your organization type';
    case 'OrganizationIndustries':
      return 'Add your organization industries';
    case 'Services':
      return 'Add your services';
    case 'OrganizationProfile':
      return 'Complete your organization profile';
    case 'InviteOrganizationMembers':
      return 'Invite your team';
    default:
      return 'Unknown step';
  }
}

export const setStepCompleted = (prev: OnboardingPart, step: OnboardingStep) => ({
  ...prev,
  steps: updateInList(
    prev.steps,
    item => item.step === step,
    () => ({ step, completenessPercentage: 100, isCompleted: true }),
  ),
});

export function calculateCompletedPercentage(onboardingSteps: Step[]) {
  return Math.ceil(
    onboardingSteps.map(it => it.completenessPercentage).reduce((a, b) => a + b, 0) / onboardingSteps.length,
  );
}

/**
 * The completeness parts each checklist step writes.
 *
 * Steps are coarser than parts — `CompanyInfo` alone writes three of them, and its dialog also asks
 * for a team size that scores nowhere. That mismatch is why a community-scored view drives its rows
 * off parts instead of steps: a community enabling `EstablishedYear` but not `Location` cannot be
 * served by a single "general company information" row.
 *
 * Steps absent from this map move no score in any community.
 */
export const completenessPartsForStep: Partial<Record<OnboardingStep, CompletenessPart[]>> = {
  CompanyInfo: ['EstablishedYear', 'Location', 'CompanyRegistrationNumber'],
  CompanyMission: ['Mission'],
  CompanyBranding: ['Branding'],
  IndustriesAndStage: ['Industries', 'ProductStage', 'FundingStage'],
  ProblemSolutionUnfairAdvantage: ['Problem', 'Solution', 'WhyInvestInUs', 'TeamDescription'],
};

/** The steps that move nothing in this community's score, so belong outside its criteria list. */
export function stepsOutsideCriteria(steps: Step[], enabledParts: CompletenessPart[]) {
  return steps.filter(({ step }) => !(completenessPartsForStep[step] ?? []).some(part => enabledParts.includes(part)));
}
